import { FC } from 'react';
import { LoaderType, NextgenImage, useTranslation } from '@nextgen-web-framework/all';
import { Holiday } from '@commerce-fulfillment-webcomponents/sdk';
import { dayLabels } from '../../common/config/checkoutConstants';
import { ICheckoutStoreDetailsDrawerContentProps } from '../types/propTypes';
import { checkoutFeatureFlagConfig, isObjectEmpty } from '../../common/transformer';
import styles from './CheckoutStoreDetailsDrawerContent.module.scss';

export const CheckoutStoreDetailsDrawerContent: FC<ICheckoutStoreDetailsDrawerContentProps> = ({
  storeDetailsData,
  imgAlt = '',
  imgSrc = '',
  showStoreId = true,
  showLine1AsName,
}: ICheckoutStoreDetailsDrawerContentProps) => {
  const t = useTranslation();
  const storeDetailsHolidayView = checkoutFeatureFlagConfig()?.storeHolidaysListVisibility;

  const getOpenHoursInfo = () => (
    <div className={`${styles['store-details-fields']} ${styles['open-hours']}`}>
      <p className={styles['fields-label']}>{t('checkout.CheckoutPickupDrawer.openHoursLabel')}</p>
      {storeDetailsData?.schedule?.asString && (
        <p className={styles['field-value']}>{storeDetailsData?.schedule?.asString}</p>
      )}
      {!storeDetailsData?.schedule?.asString &&
        dayLabels.map((day) => (
          <p key={day} className={styles['field-value']}>
            <span className={styles['openHours-label']}>{t(`checkout.CheckoutPickupDrawer.${day}`)}</span>
            <span className={styles['openHours-value']}>
              {storeDetailsData?.schedule?.[day]?.isOpen
                ? `${storeDetailsData?.schedule?.[day]?.openingTime} - ${storeDetailsData?.schedule?.[day]?.closingTime}`
                : t('checkout.CheckoutPickupDrawer.closed')}
            </span>
          </p>
        ))}
    </div>
  );

  const getHolidaysInfo = (holidaysList: Holiday[]) => (
    <div className={`${styles['store-details-fields']} ${styles['open-hours']}`}>
      <p className={styles['fields-label']}>{t('checkout.CheckoutPickupDrawer.storeHolidaysLabel')}</p>
      {holidaysList.map((holiday) => (
        <p key={holiday?.date} className={styles['field-value']}>
          <span className={styles['openHours-label']}>{holiday?.formattedDate || holiday?.date}</span>
          <span className={styles['openHours-value']}>{holiday?.description}</span>
        </p>
      ))}
    </div>
  );

  return (
    <div
      className={`${styles['store-details-container']} ${
        storeDetailsHolidayView && styles['store-details-container-with-holidays']
      }`}
    >
      <h3 className={styles['store-details-name']}>
        <div className={styles['store-image']}>
          <NextgenImage src={imgSrc} alt={imgAlt} loaderType={LoaderType.CONTENTSTACK} />
        </div>
        {showLine1AsName ? storeDetailsData?.line1 : storeDetailsData?.name}
      </h3>
      {showStoreId && (
        <div className={styles['store-details-fields']}>
          <p className={styles['fields-label']}>{t('checkout.CheckoutPickupDrawer.storeId')}</p>
          <p className={styles['fields-value']}>{storeDetailsData?.displayCode || storeDetailsData?.id}</p>
        </div>
      )}
      <div className={styles['store-details-fields']}>
        <p className={styles['fields-label']}>{t('checkout.CheckoutPickupDrawer.storeAddress')}</p>
        <p className={styles['fields-value']}>{storeDetailsData?.addressLine}</p>
        <p className={styles['fields-value']}>{storeDetailsData?.phone}</p>
      </div>
      {storeDetailsData?.contact && (
        <div className={styles['store-details-fields']}>
          <p className={styles['fields-label']}>{t('checkout.CheckoutPickupDrawer.phone')}</p>
          <p className={styles['fields-value']}>{storeDetailsData?.contact}</p>
        </div>
      )}
      {storeDetailsData?.schedule && !isObjectEmpty(storeDetailsData?.schedule) && getOpenHoursInfo()}
      {storeDetailsHolidayView &&
        storeDetailsData?.serviceLocation?.holidays?.length > 0 &&
        getHolidaysInfo(storeDetailsData?.serviceLocation?.holidays)}
    </div>
  );
};

export default CheckoutStoreDetailsDrawerContent;
